

import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { debounce } from "lodash";
import { ReactComponent as IconSort } from "../../../assets/images/icon-sort.svg";
import { ReactComponent as ArrowRight } from "../../../assets/images/icon-arrow-right.svg";
import { ReactComponent as ArrowLeft } from "../../../assets/images/icon-arrow-left.svg";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectState } from "../../../store/slices/themeSlice";
import useCustomStyles from "../../../hooks/useCustomStyles";
import { tableStyles } from "../../../assets/styles/ClientsStyles/tableStyles";
import StatusDropdown from "./StatusDropdown";
import ColumnInfo from "../../../components/ColumnInfo";

interface TableColumn {
  key: string;
  label: string;
  width?: string | number;
  isSortable?: boolean;
  /** Shown in an ⓘ tooltip next to the header label. */
  description?: string;
}

interface TableProps {
  columns: TableColumn[];
  data: Record<string, any>[];
  dropdownStatus?: {
    key: string;
    options: { value: string; label: string }[];
    onChange: (rowData: Record<string, any>, newValue: string) => void;
  }[];
  onRowClick?: (rowData: Record<string, any>) => void;
  link?: string;
  handleNavigate?: (rowData: Record<string, any>) => void;
  /** Optional trailing cell of per-row action controls. Clicks inside it
   * are stopped from bubbling to onRowClick. */
  renderRowActions?: (rowData: Record<string, any>) => React.ReactNode;
  /** Optional per-row inline style (e.g. dim a canceled row). */
  rowStyle?: (rowData: Record<string, any>) => React.CSSProperties | undefined;

  // Pagination props
  pagination?: boolean;
  currentPage?: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  itemsPerPage?: number;

  // Search props
  onSearch?: (searchTerm: string) => void;
  isSearching?: boolean;
}

const DynamicTable: React.FC<TableProps> = ({
  columns,
  data,
  onRowClick,
  handleNavigate,
  link,
  renderRowActions,
  rowStyle,
  dropdownStatus,
  pagination = true,
  currentPage: externalCurrentPage,
  onPageChange,
  itemsPerPage: externalItemsPerPage = 10,
  totalPages,
  onSearch,
  isSearching = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" }>({
    key: "",
    direction: "asc",
  });
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { theme } = useSelector(selectState);
  const classes = useCustomStyles(tableStyles, theme);

  // Use ref to store the latest onSearch callback
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  // Use ref to preserve input value across re-renders
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTermRef = useRef<string>("");

  const currentPage = externalCurrentPage || localCurrentPage;
  const itemsPerPage = externalItemsPerPage;

  function truncateString({ str, limit = 20 }: { str: string; limit?: number }): string {
    if (str && str.length > limit) {
      return str.slice(0, limit) + "...";
    }
    return str;
  }

  // Stable debounced function - no dependencies that change
  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        if (onSearchRef.current) {
          onSearchRef.current(term);
        }
      }, 500),
    [] // Empty dependency array - created once
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      searchTermRef.current = value;
      setSearchTerm(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  // Sort logic - prioritize matches starting with search term
  const sortedData = useMemo(() => {
    let sorted = [...data];

    // If searching, sort by relevance first
    if (searchTermRef.current) {
      const term = searchTermRef.current.toLowerCase();

      sorted.sort((a, b) => {
        const aClientId = (a.clientId || "").toLowerCase();
        const bClientId = (b.clientId || "").toLowerCase();
        const aName = (a.name || "").toLowerCase();
        const bName = (b.name || "").toLowerCase();

        // Check if starts with term
        const aStartsWithClientId = aClientId.startsWith(term);
        const bStartsWithClientId = bClientId.startsWith(term);
        const aStartsWithName = aName.startsWith(term);
        const bStartsWithName = bName.startsWith(term);

        const aStartsWith = aStartsWithClientId || aStartsWithName;
        const bStartsWith = bStartsWithClientId || bStartsWithName;

        // Starts with term comes first
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        // If both start with term or both don't, sort by position of term
        const aClientIdIndex = aClientId.indexOf(term);
        const bClientIdIndex = bClientId.indexOf(term);
        const aNameIndex = aName.indexOf(term);
        const bNameIndex = bName.indexOf(term);

        // Get earliest match position (-1 means not found, treat as Infinity)
        const aMinIndex = Math.min(
          aClientIdIndex === -1 ? Infinity : aClientIdIndex,
          aNameIndex === -1 ? Infinity : aNameIndex
        );
        const bMinIndex = Math.min(
          bClientIdIndex === -1 ? Infinity : bClientIdIndex,
          bNameIndex === -1 ? Infinity : bNameIndex
        );

        if (aMinIndex !== bMinIndex) {
          return aMinIndex - bMinIndex;
        }

        // If same position, sort alphabetically by clientId
        return aClientId.localeCompare(bClientId);
      });
    }

    // Apply column sorting on top if set
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue == null) return 1;
        if (bValue == null) return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortConfig.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return sorted;
  }, [data, sortConfig, searchTerm]); // Include searchTerm to trigger re-sort

  const totalPagesLocal = Math.ceil(totalPages / itemsPerPage);

  const paginate = (pageNumber: number) => {
    if (onPageChange) {
      onPageChange(pageNumber);
    } else {
      setLocalCurrentPage(pageNumber);
    }
  };

  const handleSort = (key: string) => {
    if (!columns.find((c) => c.key === key)?.isSortable) return;

    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleRowClick = (rowData: Record<string, any>) => {
    if (onRowClick) onRowClick(rowData);
    else if (handleNavigate) handleNavigate(rowData);
    else if (link) navigate(link);
  };

  return (
    <div className={classes.container}>
      {/* Search bar */}
      <div className={classes.filterBar}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search"
          className={classes.searchInput}
          value={searchTermRef.current}
          onChange={handleSearch}
        />
        {isSearching && <span className={classes.searchingIndicator}>Searching...</span>}
      </div>

      {/* Table */}
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead>
            <tr className={classes.tableHeader}>
              {columns.map(({ key, label, width, isSortable, description }) => (
                <th
                  key={key}
                  className={classes.tableHeaderCell}
                  style={{
                    width: width || "auto",
                    cursor: isSortable ? "pointer" : "default",
                  }}
                  onClick={() => handleSort(key)}
                >
                  <div className={classes.headerContent}>
                    <span className={classes.headerLabel}>{label}</span>
                    {description && <ColumnInfo label={label} description={description} />}
                    {isSortable && <IconSort width={18} height={18} />}
                  </div>
                </th>
              ))}
              <td className={`${classes.actionCell} ${classes.tableCell}`} />
            </tr>
          </thead>

          <tbody>
            {isSearching && (<span className={classes.searchingIndicator}>Searching...</span>)}
            {!isSearching && sortedData.map((item, index) => (
              <tr
                key={item.id || index}
                className={classes.tableRow}
                style={rowStyle?.(item)}
                onClick={() => handleRowClick(item)}
              >
                {columns.map(({ key }) => (
                  <td key={key} className={classes.tableCell}>
                    {dropdownStatus?.some((d) => d.key === key) ? (
                      <StatusDropdown
                        value={item[key]}
                        options={
                          dropdownStatus.find((d) => d.key === key)?.options || []
                        }
                        onChange={(newValue) =>
                          dropdownStatus
                            ?.find((d) => d.key === key)
                            ?.onChange(item, newValue)
                        }
                      />
                    ) : (
                      truncateString({ str: item[key], limit: 18 })
                    )}
                  </td>
                ))}
                {renderRowActions && (
                  <td
                    className={`${classes.actionCell} ${classes.tableCell}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderRowActions(item)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination - hide when searching */}
        {pagination && 
         !searchTerm && 
        (
          <div className={classes.paginationContainer}>
            {currentPage > 1 && (
              <button
                onClick={() => paginate(currentPage - 1)}
                className={classes.paginationButton}
              >
                <ArrowLeft width={15} height={15} />
              </button>
            )}
            <span className={classes.pageInfo}>
              Page {currentPage} of {totalPagesLocal}
            </span>
            {currentPage < totalPagesLocal && (
              <button
                onClick={() => paginate(currentPage + 1)}
                className={classes.paginationButton}
              >
                <ArrowRight width={15} height={15} />
              </button>
            )}
          </div>
        )}

        {/* No results */}
        {sortedData.length === 0 && (
          <div className={classes.noResults}>
            No results found {searchTerm ? `for "${searchTerm}"` : ""}
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicTable;


// import React, { useState, useMemo } from "react";
// import { ReactComponent as IconSort } from "../../../assets/images/icon-sort.svg";
// import { ReactComponent as ArrowRight } from "../../../assets/images/icon-arrow-right.svg";
// import { ReactComponent as ArrowLeft } from "../../../assets/images/icon-arrow-left.svg";
// import { useNavigate } from "react-router-dom";
// import { useSelector } from "react-redux";
// import { selectState } from "../../../store/slices/themeSlice";
// import useCustomStyles from "../../../hooks/useCustomStyles";
// import { tableStyles } from "../../../assets/styles/ClientsStyles/tableStyles";
// import StatusDropdown from "./StatusDropdown";

// interface TableColumn {
//   key: string;
//   label: string;
//   width?: string | number;
//   isSortable?: boolean;
// }

// interface TableProps {
//   columns: TableColumn[];
//   data: Record<string, any>[];
//   dropdownStatus?: {
//     key: string;
//     options: { value: string; label: string }[];
//     onChange: (rowData: Record<string, any>, newValue: string) => void;
//   }[];
//   onRowClick?: (rowData: Record<string, any>) => void;
//   link?: string;
//   handleNavigate?: (rowData: Record<string, any>) => void;

//   // Pagination props
//   pagination?: boolean;
//   currentPage?: number;
//   totalPages: number;
//   onPageChange?: (page: number) => void;
//   itemsPerPage?: number;
// }

// const DynamicTable: React.FC<TableProps> = ({
//   columns,
//   data,
//   onRowClick,
//   handleNavigate,
//   link,
//   dropdownStatus,
//   pagination = true,
//   currentPage: externalCurrentPage,
//   onPageChange,
//   itemsPerPage: externalItemsPerPage = 10,
//   totalPages,
// }) => {
//   const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" }>({
//     key: "",
//     direction: "asc",
//   });
//   const [localCurrentPage, setLocalCurrentPage] = useState(1);
//   const navigate = useNavigate();
//   const { theme } = useSelector(selectState);
//   const classes = useCustomStyles(tableStyles, theme);

//   const currentPage = externalCurrentPage || localCurrentPage;
//   const itemsPerPage = externalItemsPerPage;

//   function truncateString({ str, limit = 20 }: { str: string; limit?: number }): string {
//     if (str && str.length > limit) {
//       return str.slice(0, limit) + "...";
//     }
//     return str;
//   }

//   const sortedData = useMemo(() => {
//     if (!sortConfig.key) return data;

//     return [...data].sort((a, b) => {
//       let aValue = a[sortConfig.key];
//       let bValue = b[sortConfig.key];

//       if (aValue == null) return 1;
//       if (bValue == null) return -1;

//       if (typeof aValue === "string" && typeof bValue === "string") {
//         return sortConfig.direction === "asc"
//           ? aValue.localeCompare(bValue)
//           : bValue.localeCompare(aValue);
//       }

//       if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
//       return 0;
//     });
//   }, [data, sortConfig]);

//   const totalPagesLocal = Math.ceil(totalPages / itemsPerPage);

//   const paginate = (pageNumber: number) => {
//     if (onPageChange) {
//       onPageChange(pageNumber);
//     } else {
//       setLocalCurrentPage(pageNumber);
//     }
//   };

//   const handleSort = (key: string) => {
//     if (!columns.find((c) => c.key === key)?.isSortable) return;

//     let direction: "asc" | "desc" = "asc";
//     if (sortConfig.key === key && sortConfig.direction === "asc") {
//       direction = "desc";
//     }
//     setSortConfig({ key, direction });
//   };

//   const handleRowClick = (rowData: Record<string, any>) => {
//     if (onRowClick) onRowClick(rowData);
//     else if (handleNavigate) handleNavigate(rowData);
//     else if (link) navigate(link);
//   };

//   return (
//     <div className={classes.tableWrapper}>
//       <table className={classes.table}>
//         <thead>
//           <tr className={classes.tableHeader}>
//             {columns.map(({ key, label, width, isSortable }) => (
//               <th
//                 key={key}
//                 className={classes.tableHeaderCell}
//                 style={{
//                   width: width || "auto",
//                   cursor: isSortable ? "pointer" : "default",
//                 }}
//                 onClick={() => handleSort(key)}
//               >
//                 <div className={classes.headerContent}>
//                   <span className={classes.headerLabel}>{label}</span>
//                   {isSortable && <IconSort width={18} height={18} />}
//                 </div>
//               </th>
//             ))}
//             <td className={`${classes.actionCell} ${classes.tableCell}`} />
//           </tr>
//         </thead>

//         <tbody>
//           {sortedData.map((item, index) => (
//             <tr
//               key={item.id || index}
//               className={classes.tableRow}
//               onClick={() => handleRowClick(item)}
//             >
//               {columns.map(({ key }) => (
//                 <td key={key} className={classes.tableCell}>
//                   {dropdownStatus?.some((d) => d.key === key) ? (
//                     <StatusDropdown
//                       value={item[key]}
//                       options={
//                         dropdownStatus.find((d) => d.key === key)?.options || []
//                       }
//                       onChange={(newValue) =>
//                         dropdownStatus
//                           ?.find((d) => d.key === key)
//                           ?.onChange(item, newValue)
//                       }
//                     />
//                   ) : (
//                     truncateString({ str: item[key], limit: 18 })
//                   )}
//                 </td>
//               ))}
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       {/* Pagination */}
//       {pagination && totalPagesLocal > 1 && (
//         <div className={classes.paginationContainer}>
//           {currentPage > 1 && (
//             <button
//               onClick={() => paginate(currentPage - 1)}
//               className={classes.paginationButton}
//             >
//               <ArrowLeft width={15} height={15} />
//             </button>
//           )}
//           <span className={classes.pageInfo}>
//             Page {currentPage} of {totalPagesLocal}
//           </span>
//           {currentPage < totalPagesLocal && (
//             <button
//               onClick={() => paginate(currentPage + 1)}
//               className={classes.paginationButton}
//             >
//               <ArrowRight width={15} height={15} />
//             </button>
//           )}
//         </div>
//       )}

//       {/* No results */}
//       {sortedData.length === 0 && (
//         <div className={classes.noResults}>No results found</div>
//       )}
//     </div>
//   );
// };

// export default DynamicTable;